import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AuthService } from '@spartacus/core';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, filter, finalize, share, switchMap } from 'rxjs/operators';
import { CartActions } from '../../../features/cart/base/store/actions';
import { UserIdService } from '../../auth';
import { AccountingCode, AccountingCodeUserInfo, DataImport, DataImportStatus, Pagination, SearchParams } from '../../model';
import { withdrawOn } from '../../util';
import { AccountingCodesAdminConnector } from '../connectors/accounting-codes-admin-connector';
import { AccountingCodesAdminFacade } from '../facades';
import { UserActions } from '../store/actions';
import { UserSelectors } from '../store/selectors';
import { StateWithUser } from '../store/user-state';

@Injectable()
export class AccountingCodesAdminService implements AccountingCodesAdminFacade {
  constructor(
    private authService: AuthService,
    private store: Store<StateWithUser>,
    private userIdService: UserIdService,
    private accountingCodesConnector: AccountingCodesAdminConnector
  ) {}

  search(searchParams: SearchParams): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.SearchAccountingCodesAdmin({ userId, searchParams })),
      () => {}
    );
  }

  getSearchResult(searchParams: SearchParams): Observable<AccountingCode[] | undefined> {
    const isUserLoggedIn$ = this.authService.isUserLoggedIn().pipe(share());

    return isUserLoggedIn$.pipe(
      filter(Boolean),
      switchMap(() => this.store.select(UserSelectors.AccountingCodesAdminSelectors.getAdminSearchResultEntries(searchParams))),
      withdrawOn(isUserLoggedIn$.pipe(filter((isLoggedIn) => !isLoggedIn)))
    );
  }

  getSearchResultPagination(searchParams: SearchParams): Observable<Pagination | undefined> {
    return this.store.select(UserSelectors.AccountingCodesAdminSelectors.getAdminSearchPaginationSelector(searchParams));
  }

  getSearchPageResultsLoading(searchParams: SearchParams): Observable<boolean> {
    return this.store.select(UserSelectors.AccountingCodesAdminSelectors.getSearchPageResultsLoading(searchParams));
  }

  create(accountingCode: AccountingCode, searchParams: SearchParams): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.CreateAccountingCodeAdmin({ userId, accountingCode, searchParams })),
      () => {}
    );
  }

  update(accountingCode: AccountingCode): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.UpdateAccountingCodeAdmin({ userId, accountingCode })),
      () => {}
    );
  }

  delete(code: string, searchParams: SearchParams): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.DeleteAccountingCodeAdmin({ userId, code, searchParams })),
      () => {}
    );
  }

  exportAsExcel(): Observable<Blob> {
    return this.userIdService.takeUserId(true).pipe(
      switchMap((userId) => this.accountingCodesConnector.export(userId)),
      catchError(() => EMPTY)
    );
  }

  importViaExcel(file: File): Observable<number> {
    let isNoDataImported = false;

    return this.userIdService.takeUserId(true).pipe(
      switchMap((userId) => this.accountingCodesConnector.import(userId, file)),
      catchError((error: DataImport | any) => {
        isNoDataImported = error?.status !== DataImportStatus.IMPORTED_WITH_ERRORS;
        return throwError(error);
      }),
      finalize(() => {
        if (!isNoDataImported) {
          // Reloading cart when admin uploads new accounting code connections, to make sure we display up-to-date accounting code values from entries when user opens the cart afterwards.
          this.store.dispatch(new CartActions.ResetCartDetails());
        }
      })
    );
  }

  loadAccountingCodeUserInfo(): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new UserActions.LoadAccountingCodeUserInfo(userId)),
      () => {}
    );
  }

  getAccountingCodeUserInfo(): Observable<AccountingCodeUserInfo> {
    return this.store.select(UserSelectors.AccountingCodesAdminSelectors.getAccountingCodeUserInfo);
  }
}
